import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, Loader2, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const VERFAHREN_LIST = [
  "Verzinken", "Trommelverzinken", "Zink-Nickel", "Zink-Mangan-Phosphatieren",
  "CNS-Beizen", "CNS-Elektropolieren", "Schleifen · Bürsten",
  "Brünieren", "Vergolden", "Chemisch-Nickel", "Galvanisch-Nickel",
];

function detectPrefill(messages) {
  const allText = messages.map((m) => m.content).join("\n");
  const verfahren =
    VERFAHREN_LIST.find((v) =>
      new RegExp(v.replace(/[·.]/g, ".?").replace(/\s+/g, "\\s*"), "i").test(allText)
    ) || "";
  const lastUser = [...messages].reverse().find((m) => m.role === "user")?.content || "";
  return { service_interest: verfahren, message: lastUser.slice(0, 1500) };
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "fk_assistant_session";

const COPY = {
  de: {
    open: "KI-Berater öffnen", close: "KI-Berater schliessen",
    title: "KI-Galvanik-Berater",
    online: "Online · Claude Sonnet 4.5",
    reset: "Reset",
    thinking: "Berater denkt nach…",
    suggestions_label: "Oder fragen Sie zum Beispiel",
    to_offerte: "Antworten ins Offerte-Formular übernehmen",
    placeholder: "Fragen Sie den Berater…",
    send_aria: "Senden",
    powered: "Powered by Claude · Antworten ersetzen keine verbindliche Offerte",
    err_unavailable: "KI-Berater ist gerade nicht erreichbar.",
    err_apology: (detail) => `Entschuldigung — ${detail}\n\nAlternativ erreichen Sie unser Sekretariat unter +41 81 723 16 42.`,
    greeting: "Grüezi 👋 Ich bin der KI-Galvanik-Berater der Fritz Kamm AG. Fragen Sie mich zu unseren 11 Verfahren — von Verzinken bis CNS-Elektropolieren. Womit darf ich starten?",
    suggestions: [
      "Welches Verfahren für Aluminium-Gehäuse?",
      "Was ist der Unterschied zwischen Verzinken und Zink-Nickel?",
      "Eignet sich Trommelverzinken für 5000 Stk. M8-Schrauben?",
      "Brauche ich CNS-Beizen vor dem Elektropolieren?",
    ],
  },
  en: {
    open: "Open AI advisor", close: "Close AI advisor",
    title: "AI Plating Advisor",
    online: "Online · Claude Sonnet 4.5",
    reset: "Reset",
    thinking: "Advisor is thinking…",
    suggestions_label: "Or ask, for example",
    to_offerte: "Send answers to the quote form",
    placeholder: "Ask the advisor…",
    send_aria: "Send",
    powered: "Powered by Claude · Replies do not replace a binding quote",
    err_unavailable: "The AI advisor is currently unavailable.",
    err_apology: (detail) => `Sorry — ${detail}\n\nAlternatively, you can reach our office at +41 81 723 16 42.`,
    greeting: "Hello 👋 I'm the AI plating advisor at Fritz Kamm AG. Ask me anything about our 11 processes — from electrogalvanizing to stainless-steel electropolishing. How can I help?",
    suggestions: [
      "Which process for aluminium housings?",
      "What's the difference between zinc plating and zinc-nickel?",
      "Is barrel zinc plating suitable for 5,000 pcs of M8 screws?",
      "Do I need stainless-steel pickling before electropolishing?",
    ],
  },
  fr: {
    open: "Ouvrir le conseiller IA", close: "Fermer le conseiller IA",
    title: "Conseiller IA Galvanoplastie",
    online: "En ligne · Claude Sonnet 4.5",
    reset: "Réinitialiser",
    thinking: "Le conseiller réfléchit…",
    suggestions_label: "Ou demandez par exemple",
    to_offerte: "Reprendre les réponses dans le formulaire de devis",
    placeholder: "Posez votre question au conseiller…",
    send_aria: "Envoyer",
    powered: "Propulsé par Claude · Les réponses ne remplacent pas un devis ferme",
    err_unavailable: "Le conseiller IA n'est pas disponible actuellement.",
    err_apology: (detail) => `Désolé — ${detail}\n\nVous pouvez aussi joindre notre bureau au +41 81 723 16 42.`,
    greeting: "Bonjour 👋 Je suis le conseiller IA en galvanoplastie de Fritz Kamm SA. Posez-moi vos questions sur nos 11 procédés — du zingage au polissage électrolytique de l'inox. Comment puis-je vous aider ?",
    suggestions: [
      "Quel procédé pour des boîtiers en aluminium ?",
      "Quelle est la différence entre zingage et zinc-nickel ?",
      "Le zingage au tonneau convient-il pour 5000 vis M8 ?",
      "Faut-il décaper l'inox avant un polissage électrolytique ?",
    ],
  },
  it: {
    open: "Apri il consulente IA", close: "Chiudi il consulente IA",
    title: "Consulente IA Galvanica",
    online: "Online · Claude Sonnet 4.5",
    reset: "Reset",
    thinking: "Il consulente sta riflettendo…",
    suggestions_label: "Oppure chieda ad esempio",
    to_offerte: "Trasferisci le risposte nel modulo offerta",
    placeholder: "Chieda al consulente…",
    send_aria: "Invia",
    powered: "Powered by Claude · Le risposte non sostituiscono un'offerta vincolante",
    err_unavailable: "Il consulente IA non è al momento raggiungibile.",
    err_apology: (detail) => `Ci scusiamo — ${detail}\n\nIn alternativa può raggiungere la nostra segreteria al +41 81 723 16 42.`,
    greeting: "Buongiorno 👋 Sono il consulente IA per la galvanica di Fritz Kamm SA. Mi chieda dei nostri 11 procedimenti — dalla zincatura all'elettrolucidatura dell'inox. Da dove iniziamo?",
    suggestions: [
      "Quale procedimento per scocche in alluminio?",
      "Differenza tra zincatura e zinco-nichel?",
      "La zincatura a rotobarile va bene per 5000 viti M8?",
      "Serve decapare l'inox prima dell'elettrolucidatura?",
    ],
  },
};

export const Assistant = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) || null : null
  );
  const [messages, setMessages] = useState([{ role: "assistant", content: c.greeting }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Update greeting if language switches and there's no user interaction yet
  useEffect(() => {
    setMessages((prev) => {
      const hasUser = prev.some((m) => m.role === "user");
      if (hasUser) return prev;
      return [{ role: "assistant", content: c.greeting }];
    });
  }, [c.greeting]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/assistant/chat`, {
        session_id: sessionId,
        messages: next.filter((m) => m.role !== "system").map(({ role, content }) => ({ role, content })),
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || c.err_unavailable;
      setMessages([...next, { role: "assistant", content: c.err_apology(detail) }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    setMessages([{ role: "assistant", content: c.greeting }]);
    setSessionId(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const sendToOfferte = () => {
    const prefill = detectPrefill(messages);
    window.dispatchEvent(new CustomEvent("fk:prefill-contact", { detail: prefill }));
    setOpen(false);
    setTimeout(() => {
      const el = document.getElementById("kontakt");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  const hasUserMessage = messages.some((m) => m.role === "user");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="assistant-toggle"
        aria-label={open ? c.close : c.open}
        className={`fixed z-[60] bottom-6 left-6 w-14 h-14 flex items-center justify-center transition-all duration-300 shadow-2xl ${
          open ? "bg-zinc-900 text-white border border-white/20" : "bg-white text-black hover:scale-105"
        }`}
      >
        {open ? (
          <X className="w-5 h-5" strokeWidth={1.5} />
        ) : (
          <>
            <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0E0E10] pulse-dot" />
          </>
        )}
      </button>

      <div
        data-testid="assistant-panel"
        className={`fixed z-[59] bottom-24 left-6 right-6 sm:right-auto sm:w-[420px] max-h-[min(640px,calc(100vh-140px))] bg-[#121214] border border-white/10 shadow-2xl flex flex-col transition-all duration-400 origin-bottom-left ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative w-9 h-9 bg-white text-black flex items-center justify-center">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-sm font-semibold tracking-tight text-white">
                {c.title}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                {c.online}
              </div>
            </div>
          </div>
          <button type="button" onClick={reset} data-testid="assistant-reset" className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 hover:text-white transition-colors">
            {c.reset}
          </button>
        </div>

        <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((m, i) => (
            <div key={i} data-testid={`assistant-message-${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap px-3.5 py-2.5 ${m.role === "user" ? "bg-white text-black" : "bg-[#1a1a1d] text-zinc-200 border border-white/5"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div data-testid="assistant-typing" className="flex justify-start">
              <div className="px-3.5 py-2.5 bg-[#1a1a1d] border border-white/5 text-zinc-400 text-sm flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {c.thinking}
              </div>
            </div>
          )}

          {messages.length <= 1 && !sending && (
            <div className="pt-2">
              <div className="overline mb-2">{c.suggestions_label}</div>
              <div className="flex flex-col gap-2">
                {c.suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)} data-testid={`assistant-suggestion-${s.slice(0, 12)}`} className="text-left text-[13px] text-zinc-300 hover:text-white border border-white/10 hover:border-white/30 hover:bg-white/[0.04] px-3 py-2 transition-all">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-white/10">
          {hasUserMessage && (
            <button type="button" onClick={sendToOfferte} data-testid="assistant-to-offerte" className="w-full mb-2 inline-flex items-center justify-between gap-2 border border-white/20 hover:bg-white/5 hover:border-white/40 px-3 py-2.5 text-[13px] text-zinc-200 transition-colors">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                {c.to_offerte}
              </span>
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
          <div className="flex items-end gap-2 bg-[#0E0E10] border border-white/10 focus-within:border-white/40 transition-colors">
            <textarea ref={inputRef} data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} placeholder={c.placeholder} rows={1} className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 px-3 py-2.5 outline-none resize-none max-h-32" />
            <button type="button" onClick={() => send()} disabled={!input.trim() || sending} data-testid="assistant-send" className="m-1 w-9 h-9 flex items-center justify-center bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label={c.send_aria}>
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="mt-2 text-[10px] text-zinc-500 text-center">{c.powered}</div>
        </div>
      </div>
    </>
  );
};

export default Assistant;
